import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Link, useNavigate, useRouteContext } from "@tanstack/react-router";
import { CheckCircle2, ChevronRight, Dumbbell, User } from "lucide-react";
import React, { useState } from "react";

interface ProgramActivationWizardProps {
  children: React.ReactNode;
  onConfirm: () => void;
  isPending: boolean;
  programName: string;
  hasActiveProgram?: boolean;
}

export function ProgramActivationWizard({
  children,
  onConfirm,
  isPending,
  programName,
  hasActiveProgram = false,
}: ProgramActivationWizardProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const { user } = useRouteContext({ from: "/_app" });
  const navigate = useNavigate();

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset step when closed
      setTimeout(() => setStep(hasActiveProgram ? 1 : 2), 300);
    }
  };

  // Auth Check Wrapper
  const handleTriggerClick = (e: React.MouseEvent) => {
    if (!user) {
      e.preventDefault();
      setOpen(true);
      // If not logged in, we show the login prompt
      return;
    }

    // If no active program, skip step 1 (Warning) and go to step 2 (Profile)
    if (!hasActiveProgram) {
      setStep(2);
    } else {
      setStep(1);
    }
  };

  const renderContent = () => {
    if (!user) {
      return (
        <>
          <DialogHeader>
            <DialogTitle>Giriş Yapmalısın</DialogTitle>
            <DialogDescription>
              Bir antrenman programına başlamak için önce giriş yapman veya
              kayıt olman gerekiyor.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center gap-4 py-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <User className="h-8 w-8 text-primary" />
            </div>
            <p className="text-muted-foreground text-sm">
              Hesabın varsa giriş yapabilir, yoksa saniyeler içinde yeni hesap
              oluşturabilirsin.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              İptal
            </Button>
            <Button asChild onClick={() => setOpen(false)}>
              <Link to="/password/login">Giriş Yap / Kayıt Ol</Link>
            </Button>
          </DialogFooter>
        </>
      );
    }

    if (step === 1) {
      return (
        <>
          <DialogHeader>
            <DialogTitle>Programı Değiştir</DialogTitle>
            <DialogDescription>
              Yeni bir programa başlamak üzeresiniz.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
              <div className="flex items-start gap-4 p-4">
                <div className="rounded-full bg-muted p-2 text-amber-500">
                  <Dumbbell className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-medium text-sm">
                    Mevcut Programın Duraklatılacak
                  </h4>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    <strong>"{programName}"</strong> programına başladığınızda,
                    şu an aktif olan programınız duraklatılacak ve ilerlemeniz
                    güvenli bir şekilde saklanacaktır. İstediğiniz zaman eski
                    programınıza geri dönüp kaldığınız yerden devam
                    edebilirsiniz.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Vazgeç
            </Button>
            <Button onClick={() => setStep(2)}>
              Devam Et <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <DialogHeader>
            <DialogTitle>Profil Kontrolü</DialogTitle>
            <DialogDescription>
              Antrenman hedeflerinin doğru hesaplanması için önemli.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-border bg-muted/50 p-4 text-muted-foreground">
              <h4 className="mb-2 flex items-center gap-2 font-medium text-sm">
                <CheckCircle2 className="h-4 w-4" />
                1RM Verilerini Kontrol Et
              </h4>
              <p className="text-xs leading-relaxed opacity-90">
                Antrenman sırasında "Target Weight" (Hedef Ağırlık) değerlerini
                görebilmen için profilindeki 1RM (Maksimum Ağırlık) verilerinin
                dolu olması gerekir.
              </p>
            </div>

            <p className="px-4 text-center text-muted-foreground text-xs">
              Eğer daha önce girmediysen, şimdi profil sayfasına gidip
              güncelleyebilirsin.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={() => {
                navigate({
                  to: "/profile",
                });
              }}
            >
              Profile Git (Yeni Sekme)
            </Button>
            <Button onClick={() => setStep(3)}>Verilerim Tamam / Atla</Button>
          </DialogFooter>
        </>
      );
    }

    if (step === 3) {
      return (
        <>
          <DialogHeader>
            <DialogTitle>Hazırsan Başlayalım!</DialogTitle>
            <DialogDescription>
              <strong>"{programName}"</strong> programı senin için hazırlanıyor.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="zoom-in mb-4 flex h-20 w-20 animate-in items-center justify-center rounded-full bg-muted text-primary duration-300">
              <Dumbbell className="h-10 w-10" />
            </div>
            <h3 className="font-semibold text-lg">İyi Antrenmanlar!</h3>
            <p className="mt-2 max-w-[280px] text-muted-foreground text-sm">
              Disiplinli çalış, gelişimini takip et ve hedeflerine ulaş.
            </p>
            <p className="mt-4 max-w-[320px] text-[11px] text-muted-foreground/80 leading-relaxed">
              Programı başlatarak{" "}
              <Link
                to="/privacy"
                search={{ tab: "kvkk" }}
                target="_blank"
                className="text-foreground underline underline-offset-2 hover:text-primary"
              >
                KVKK
              </Link>
              ,{" "}
              <Link
                to="/privacy"
                search={{ tab: "videos" }}
                target="_blank"
                className="text-foreground underline underline-offset-2 hover:text-primary"
              >
                Video Açık Rızası
              </Link>{" "}
              ve{" "}
              <Link
                to="/privacy"
                search={{ tab: "terms" }}
                target="_blank"
                className="text-foreground underline underline-offset-2 hover:text-primary"
              >
                Kullanım Koşulları
              </Link>
              'nı kabul etmiş sayılırsınız.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Geri Dön
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isPending}
              className={cn(isPending && "opacity-80")}
            >
              {isPending ? "Başlatılıyor..." : "Programı Başlat"}
            </Button>
          </DialogFooter>
        </>
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild onClick={handleTriggerClick}>
        {children}
      </DialogTrigger>
      <DialogContent className="transition-all duration-300 sm:max-w-md">
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
